import React, { useState } from 'react';
import {
  Modal,
  View,
  TouchableOpacity,
  StyleSheet,
  Dimensions,
  SafeAreaView,
  ActivityIndicator,
  Text,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, fontSizes, spacing } from '../styles/theme';
import { ZoomableImage } from './ZoomableImage';

const { width: screenWidth, height: screenHeight } = Dimensions.get('window');

interface ImageEnlargementModalProps {
  visible: boolean;
  imageUri: string;
  title?: string;
  onClose: () => void;
  onError?: () => void;
  onLoad?: () => void;
}

export const ImageEnlargementModal: React.FC<ImageEnlargementModalProps> = ({
  visible,
  imageUri,
  title,
  onClose,
  onError,
  onLoad,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleImageError = () => {
    setImageError(true);
    onError?.();
  };

  const handleClose = () => {
    setImageLoaded(false);
    setImageError(false);
    onClose();
  };

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="fade"
      onRequestClose={handleClose}
      statusBarTranslucent={true}
    >
      <SafeAreaView style={styles.container}>
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity onPress={handleClose} style={styles.closeButton}>
            <Ionicons name="close" size={28} color={colors.background} />
          </TouchableOpacity>
          {title && <Text style={styles.title} numberOfLines={1}>{title}</Text>}
          <View style={styles.placeholder} />
        </View>

        {/* Image Container — #168: real zoom/pan via ZoomableImage.
            The old `<ScrollView maximumZoomScale>` was an iOS-native-only
            no-op on web, so the deployed app had NO working zoom/pan. */}
        <View style={styles.scrollContainer}>
          {!imageLoaded && !imageError && (
            <View style={styles.loadingOverlay} pointerEvents="none">
              <ActivityIndicator size="large" color={colors.background} />
              <Text style={styles.loadingText}>Loading image...</Text>
            </View>
          )}

          {imageError ? (
            <View style={styles.errorContainer}>
              <Ionicons name="image" size={64} color={colors.background} />
              <Text style={styles.errorText}>Failed to load image</Text>
              <TouchableOpacity
                style={styles.retryButton}
                onPress={() => {
                  setImageError(false);
                  setImageLoaded(false);
                }}
              >
                <Text style={styles.retryButtonText}>Try Again</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <ZoomableImage
              uri={imageUri}
              resetKey={imageUri}
              onLoad={() => {
                setImageLoaded(true);
                onLoad?.();
              }}
              onError={handleImageError}
              testID="enlargement-zoomable-image"
            />
          )}
        </View>

        {/* Footer with instructions */}
        <View style={styles.footer}>
          <Text style={styles.instructionText}>
            Pinch or scroll to zoom · drag to pan · double-tap to reset
          </Text>
        </View>
      </SafeAreaView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.95)',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    zIndex: 10,
  },
  closeButton: {
    padding: spacing.sm,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
  title: {
    flex: 1,
    fontSize: fontSizes.lg,
    fontWeight: '600',
    color: colors.background,
    textAlign: 'center',
    marginHorizontal: spacing.md,
  },
  placeholder: {
    width: 44, // Same width as close button for balance
  },
  scrollContainer: {
    flex: 1,
  },
  imageContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: screenHeight * 0.8,
  },
  imageWrapper: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  image: {
    borderRadius: 8,
  },
  loadingContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
  },
  // #168 — overlay (not a layout sibling) so it sits ON TOP of the
  // ZoomableImage while the photo decodes, then fades out via state.
  loadingOverlay: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 5,
  },
  loadingText: {
    fontSize: fontSizes.base,
    color: colors.background,
    marginTop: spacing.md,
  },
  errorContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
    flex: 1,
  },
  errorText: {
    fontSize: fontSizes.lg,
    color: colors.background,
    textAlign: 'center',
    marginTop: spacing.md,
    marginBottom: spacing.lg,
  },
  retryButton: {
    backgroundColor: colors.primary,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderRadius: 8,
  },
  retryButtonText: {
    fontSize: fontSizes.base,
    color: colors.background,
    fontWeight: '600',
  },
  footer: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    alignItems: 'center',
  },
  instructionText: {
    fontSize: fontSizes.sm,
    color: 'rgba(255, 255, 255, 0.7)',
    textAlign: 'center',
  },
}); 